<template>
  <div class="modal-card card">
    <div class="card-header">
      <h4 class="card-header-title">Audience Composition</h4>
      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
    </div>
    <div class="card-body">
      <!-- Configuration Form -->
      <form v-if="!hasResults" @submit.prevent="runHistogram">
        <div class="mb-3">
          <label class="form-label">Columns</label>
          <Multiselect
            v-model="selectedColumns"
            :options="availableColumns"
            mode="tags"
            :searchable="true"
            :close-on-select="false"
            placeholder="All columns (leave empty for full analysis)"
            no-options-text="No columns available"
            no-results-text="No columns match your search"
          />
          <small class="text-muted">Leave empty to analyze all columns, or select specific ones</small>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label">Query Engine</label>
            <select class="form-select" v-model="queryEngine">
              <option value="athena">Athena</option>
              <option value="analytics">Analytics</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label">Order</label>
            <select class="form-select" v-model="orderBy">
              <option value="count_desc">Count (High to Low)</option>
              <option value="count_asc">Count (Low to High)</option>
              <option value="value_asc">Value (A-Z)</option>
              <option value="value_desc">Value (Z-A)</option>
            </select>
          </div>
        </div>

        <div v-if="error" class="alert alert-danger">
          {{ error }}
        </div>
      </form>

      <!-- Results Display -->
      <div v-else>
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3 text-muted">Analyzing audience composition...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger">
          {{ error }}
          <button type="button" class="btn btn-outline-secondary btn-sm ms-3" @click="resetResults">
            <i class="fa fa-arrow-left me-1"></i>
            Try Again
          </button>
        </div>

        <!-- Results -->
        <div v-else>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">
              <i class="fa fa-chart-pie me-2"></i>
              Composition Results
            </h5>
            <div class="d-flex align-items-center gap-2">
              <div class="btn-group btn-group-sm" role="group" aria-label="Result display">
                <button
                  type="button"
                  class="btn"
                  :class="display === 'chart' ? 'btn-primary' : 'btn-outline-secondary'"
                  :disabled="chartAttributes.length === 0"
                  @click="display = 'chart'"
                >
                  <i class="fa fa-chart-pie me-1"></i>
                  Chart
                </button>
                <button
                  type="button"
                  class="btn"
                  :class="display === 'table' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="display = 'table'"
                >
                  <i class="fa fa-table-list me-1"></i>
                  Table
                </button>
              </div>
              <button type="button" class="btn btn-outline-secondary btn-sm" @click="resetResults">
                <i class="fa fa-arrow-left me-1"></i>
                Run Another
              </button>
            </div>
          </div>

          <!-- Chart view: every single attribute at once, the way the audience
               dashboard shows a composition. Combinations stay in the table -
               their values are pairs and make unreadable chart labels. -->
          <div v-if="showCharts" class="composition-charts">
            <AttributeCharts :attributes="chartAttributes" :record-total="recordTotal" />
            <p v-if="recordTotal" class="text-muted small mt-3 mb-0">
              Total records: {{ formatNumber(recordTotal) }}
            </p>
          </div>

          <template v-else>

          <!-- Tabs for Single Attributes and Combinations -->
          <ul v-if="hasBothTypes" class="nav nav-pills mb-3">
            <li class="nav-item">
              <a
                class="nav-link"
                :class="{ active: viewMode === 'single' }"
                href="#"
                @click.prevent="viewMode = 'single'"
              >
                <i class="fa fa-list me-1"></i>
                Single Attributes
                <span class="badge bg-secondary ms-1">{{ singleColumns.length }}</span>
              </a>
            </li>
            <li class="nav-item">
              <a
                class="nav-link"
                :class="{ active: viewMode === 'combination' }"
                href="#"
                @click.prevent="viewMode = 'combination'"
              >
                <i class="fa fa-table-cells me-1"></i>
                Combinations
                <span class="badge bg-secondary ms-1">{{ combinationColumns.length }}</span>
              </a>
            </li>
          </ul>

          <!-- Column Tabs (for current view mode) -->
          <ul v-if="currentViewColumns.length > 1" class="nav nav-tabs mb-3" style="flex-wrap: wrap;">
            <li v-for="col in currentViewColumns" :key="col" class="nav-item">
              <a
                class="nav-link"
                :class="{ active: activeResultTab === col }"
                href="#"
                @click.prevent="activeResultTab = col"
              >
                {{ formatColumnName(col) }}
                <span v-if="histogramResults[col]?.loading" class="spinner-border spinner-border-sm ms-1"></span>
                <span v-else-if="histogramResults[col]?.error" class="text-danger ms-1">
                  <i class="fa fa-exclamation-circle"></i>
                </span>
              </a>
            </li>
          </ul>

          <!-- Single column header when only one in view -->
          <h6 v-else-if="currentViewColumns.length === 1" class="mb-3">
            <span v-if="isComboColumn(currentViewColumns[0])">
              Columns: <strong>{{ formatColumnName(currentViewColumns[0]) }}</strong>
            </span>
            <span v-else>
              Column: <strong>{{ formatColumnName(currentViewColumns[0]) }}</strong>
            </span>
          </h6>

          <!-- Results for active tab -->
          <div v-if="activeResultData">
            <div v-if="activeResultData.loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2 text-muted">Loading...</p>
            </div>

            <div v-else-if="activeResultData.error" class="alert alert-danger">
              {{ activeResultData.error }}
            </div>

            <div v-else-if="activeResultData.data?.length === 0" class="text-center py-4 text-muted">
              No data found for this column.
            </div>

            <!-- Single column table -->
            <div v-else-if="!isComboColumn(activeResultTab)" class="table-responsive" style="max-height: 400px; overflow-y: auto;">
              <table class="table table-sm table-hover">
                <thead class="sticky-top bg-white">
                  <tr>
                    <th>Value</th>
                    <th class="text-end">Count</th>
                    <th style="width: 40%;">Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in activeResultRows" :key="index">
                    <td>{{ item.value || '(empty)' }}</td>
                    <td class="text-end">{{ formatNumber(item.count) }}</td>
                    <td>
                      <div class="distribution-cell">
                        <div class="progress" style="height: 20px;">
                          <div
                            class="progress-bar"
                            role="progressbar"
                            :style="{ width: item.percentage + '%' }"
                            :aria-valuenow="item.percentage"
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                        <span class="distribution-pct">{{ item.percentage.toFixed(1) }}%</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Combination column table -->
            <div v-else class="table-responsive" style="max-height: 400px; overflow-y: auto;">
              <table class="table table-sm table-hover">
                <thead class="sticky-top bg-white">
                  <tr>
                    <th v-for="colName in getComboColumnNames(activeResultTab)" :key="colName">
                      {{ formatAttributeName(colName) }}
                    </th>
                    <th class="text-end">Count</th>
                    <th style="width: 30%;">Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in activeResultRows" :key="index">
                    <td v-for="(val, vIndex) in splitComboValue(item.value)" :key="vIndex">
                      {{ val || '(empty)' }}
                    </td>
                    <td class="text-end">{{ formatNumber(item.count) }}</td>
                    <td>
                      <div class="distribution-cell">
                        <div class="progress" style="height: 20px;">
                          <div
                            class="progress-bar"
                            role="progressbar"
                            :style="{ width: item.percentage + '%' }"
                            :aria-valuenow="item.percentage"
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                        <span class="distribution-pct">{{ item.percentage.toFixed(1) }}%</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
            </table>
          </div>

          <div v-if="activeResultData.data?.length > 0" class="mt-3 text-muted small">
            Total records: {{ formatNumber(activeResultData.total) }}
          </div>
        </div>
          </template>
        </div>
      </div>
    </div>
    <div class="card-footer">
      <button
        v-if="!hasResults"
        type="button"
        class="btn btn-primary"
        @click="runHistogram"
        :disabled="loading"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm me-2" />
        <i v-else class="fa fa-play me-1"></i>
        Analyze Composition
      </button>
      <button
        v-if="hasResults"
        type="button"
        class="btn btn-primary"
        @click="downloadResults"
      >
        <i class="fa fa-download me-1"></i>
        Download CSV
      </button>
      <button type="button" class="btn btn-secondary ms-2" data-bs-dismiss="modal">
        {{ hasResults ? 'Close' : 'Cancel' }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Multiselect from '@vueform/multiselect'
import AttributeCharts from '../audience/AttributeCharts.vue'

export default {
  name: 'RunHistogram',
  components: {
    AttributeCharts,
    Multiselect,
  },
  props: {
    modalData: Object,
  },
  data() {
    return {
      loading: false,
      error: null,
      selectedColumns: [],
      queryEngine: 'analytics',
      orderBy: 'count_desc',
      histogramResults: {},
      hasResults: false,
      activeResultTab: null,
      runAllColumns: false,
      resultColumns: [],
      viewMode: 'single', // 'single' or 'combination'
      display: 'chart', // 'chart' or 'table'
      recordTotal: 0,
    }
  },
  computed: {
    audience() {
      return this.modalData?.audience
    },
    availableColumns() {
      return this.modalData?.columns || []
    },
    activeResultData() {
      if (!this.activeResultTab) return null
      return this.histogramResults[this.activeResultTab]
    },
    // The distribution cell needs the percentage for both the bar width and its
    // label, so work it out once per row here.
    activeResultRows() {
      const data = this.activeResultData?.data
      if (!Array.isArray(data)) return []
      return data.map(item => ({
        ...item,
        percentage: this.getPercentage(item.count, this.activeResultData.total),
      }))
    },
    // Composition is served from its own dataset alongside the audience dataset
    compositionDataset() {
      const dataset = this.audience?.audsum_dataset
      if (!dataset) return dataset
      return dataset.endsWith('-composition') ? dataset : `${dataset}-composition`
    },
    criterion() {
      return this.modalData?.criterion || '{}'
    },
    singleColumns() {
      return this.resultColumns.filter(col => !this.isComboColumn(col))
    },
    combinationColumns() {
      return this.resultColumns.filter(col => this.isComboColumn(col))
    },
    hasBothTypes() {
      return this.singleColumns.length > 0 && this.combinationColumns.length > 0
    },
    currentViewColumns() {
      if (!this.hasBothTypes) {
        return this.resultColumns
      }
      return this.viewMode === 'single' ? this.singleColumns : this.combinationColumns
    },
    // One tile per single attribute, smallest first so the grid packs tightly
    chartAttributes() {
      return this.singleColumns
        .map(name => ({ name, values: this.histogramResults[name]?.data || [] }))
        .filter(attr => attr.values.length > 0)
        .sort((a, b) => a.values.length - b.values.length)
    },
    showCharts() {
      return this.display === 'chart' && this.chartAttributes.length > 0
    },
  },
  methods: {
    ...mapActions({
      runHistogramQuery: 'audience/runHistogramQuery',
      addToast: 'addToast',
    }),
    async runHistogram() {
      this.loading = true
      this.error = null
      this.hasResults = true
      this.runAllColumns = this.selectedColumns.length === 0

      try {
        const criterion = JSON.parse(this.criterion)

        // Build query - omit attribute_names for full analysis
        const queryBody = {
          dataset: this.compositionDataset,
          criterion: criterion.criterion || criterion,
        }

        // Only add attribute_names if specific columns selected
        if (this.selectedColumns.length > 0) {
          queryBody.attribute_names = this.selectedColumns
        }

        const result = await this.runHistogramQuery({
          dataEnvironmentId: this.audience.audsum_data_environment,
          queryBody,
          queryEngine: this.queryEngine,
        })

        // Parse results - the API returns data for all columns
        this.parseMultiColumnResult(result)
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to run histogram query'
      }

      this.loading = false
    },
    parseMultiColumnResult(result) {
      // Handle audsum format: { data: { header: ["attribute_name", "attribute_value", "count"], body: [[...]] } }
      if (result?.data?.body && Array.isArray(result.data.body)) {
        const header = result.data.header || []
        const nameIndex = header.indexOf('attribute_name')
        const valueIndex = header.indexOf('attribute_value')
        const countIndex = header.indexOf('count')

        // Group results by column name
        const columnData = {}
        // The response carries the record total on its own row, marked with '-'
        // for both the attribute name and value
        let totalRecords = null
        result.data.body.forEach(row => {
          const colName = row[nameIndex !== -1 ? nameIndex : 0]
          const value = row[valueIndex !== -1 ? valueIndex : 1]
          const count = parseInt(row[countIndex !== -1 ? countIndex : 2], 10) || 0

          if (colName === '-' && value === '-') {
            totalRecords = count
            return
          }

          if (!columnData[colName]) {
            columnData[colName] = []
          }
          columnData[colName].push({ value, count })
        })

        // Get columns to process - either selected or discovered from results
        const columnsToProcess = this.runAllColumns
          ? Object.keys(columnData)
          : this.selectedColumns

        this.resultColumns = columnsToProcess

        // Update results for each column
        this.recordTotal = totalRecords !== null ? totalRecords : 0

        columnsToProcess.forEach(col => {
          const data = this.sortData(columnData[col] || [])
          // Summing the attribute counts only matches the record total when every
          // record has exactly one value for the column - fall back to it only
          // when the response left the total row out
          const total = totalRecords !== null
            ? totalRecords
            : data.reduce((sum, item) => sum + (item.count || 0), 0)
          this.histogramResults = {
            ...this.histogramResults,
            [col]: { loading: false, data, error: null, total }
          }
        })

        // Set initial view mode and active tab based on what data we have
        const singleCols = columnsToProcess.filter(c => !c.includes('{)(}'))
        const comboCols = columnsToProcess.filter(c => c.includes('{)(}'))

        if (singleCols.length > 0) {
          this.viewMode = 'single'
          this.activeResultTab = singleCols[0]
        } else if (comboCols.length > 0) {
          this.viewMode = 'combination'
          this.activeResultTab = comboCols[0]
        } else {
          this.activeResultTab = columnsToProcess[0] || null
        }
      } else {
        this.resultColumns = []
      }
    },
    resetResults() {
      this.hasResults = false
      this.histogramResults = {}
      this.activeResultTab = null
      this.resultColumns = []
      this.runAllColumns = false
      this.viewMode = 'single'
      this.display = 'chart'
      this.recordTotal = 0
    },
    // Helper methods for combo columns
    isComboColumn(colName) {
      return colName && colName.includes('{)(}')
    },
    formatColumnName(colName) {
      if (this.isComboColumn(colName)) {
        return colName.split('{)(}').map(c => this.formatAttributeName(c)).join(' + ')
      }
      return this.formatAttributeName(colName)
    },
    formatAttributeName(name) {
      if (!name) return ''
      return name
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    getComboColumnNames(colName) {
      if (!colName) return []
      return colName.split('{)(}')
    },
    splitComboValue(value) {
      if (!value) return ['']
      return value.split('{)(}')
    },
    sortData(data) {
      if (!data || data.length === 0) return data
      const sorted = [...data]
      switch (this.orderBy) {
        case 'count_desc':
          sorted.sort((a, b) => (b.count || 0) - (a.count || 0))
          break
        case 'count_asc':
          sorted.sort((a, b) => (a.count || 0) - (b.count || 0))
          break
        case 'value_asc':
          sorted.sort((a, b) => String(a.value || '').localeCompare(String(b.value || '')))
          break
        case 'value_desc':
          sorted.sort((a, b) => String(b.value || '').localeCompare(String(a.value || '')))
          break
      }
      return sorted
    },
    getPercentage(count, total) {
      if (!total || total === 0) return 0
      return (count / total) * 100
    },
    formatNumber(num) {
      if (num == null) return '-'
      return num.toLocaleString()
    },
    downloadResults() {
      // Download results for active tab or all if multiple
      if (this.resultColumns.length === 1) {
        this.downloadSingleColumnCSV(this.resultColumns[0])
      } else {
        this.downloadAllColumnsCSV()
      }
    },
    downloadSingleColumnCSV(column) {
      const resultData = this.histogramResults[column]
      if (!resultData?.data?.length) return

      const headers = ['Value', 'Count', 'Percentage']
      const rows = resultData.data.map(item => [
        `"${(item.value || '').toString().replace(/"/g, '""')}"`,
        item.count,
        this.getPercentage(item.count, resultData.total).toFixed(2) + '%',
      ])

      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
      this.triggerDownload(csv, `histogram_${column}_${new Date().toISOString().split('T')[0]}.csv`)
    },
    downloadAllColumnsCSV() {
      // Create a combined CSV with all columns
      const headers = ['Column', 'Value', 'Count', 'Percentage']
      const rows = []

      this.resultColumns.forEach(col => {
        const resultData = this.histogramResults[col]
        if (resultData?.data?.length) {
          resultData.data.forEach(item => {
            rows.push([
              `"${col}"`,
              `"${(item.value || '').toString().replace(/"/g, '""')}"`,
              item.count,
              this.getPercentage(item.count, resultData.total).toFixed(2) + '%',
            ])
          })
        }
      })

      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
      this.triggerDownload(csv, `histogram_multiple_${new Date().toISOString().split('T')[0]}.csv`)
    },
    triggerDownload(csv, filename) {
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()
      URL.revokeObjectURL(link.href)

      this.addToast({ type: 'success', message: 'CSV downloaded successfully' })
    },
  },
  watch: {
    viewMode(newMode) {
      // Update activeResultTab when switching between single and combination views
      const cols = newMode === 'single' ? this.singleColumns : this.combinationColumns
      if (cols.length > 0 && !cols.includes(this.activeResultTab)) {
        this.activeResultTab = cols[0]
      }
    },
  },
}
</script>

<style scoped>
.distribution-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.distribution-cell .progress {
  flex: 1;
}

/* Outside the bar, not inside it: the fill is too narrow to hold the label at
   small percentages and clips it, and white on the fill disappears the moment it
   spills onto the track. Out here it keeps the table's own text colour, which is
   near-black on light and light on dark. The fixed width lines the numbers up
   with each other and keeps every bar the same length. */
.distribution-pct {
  flex: 0 0 3.25rem;
  text-align: right;
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* A fraction of a percent still deserves a visible mark */
.distribution-cell .progress-bar {
  min-width: 3px;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1;
}

.nav-tabs .nav-link {
  cursor: pointer;
}

.nav-tabs .nav-link.active {
  font-weight: 600;
}

.nav-pills {
  gap: 0.5rem;
}

.nav-pills .nav-link {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.nav-pills .nav-link:not(.active) {
  background-color: #f8f9fa;
  color: #495057;
}

.nav-pills .nav-link:not(.active):hover {
  background-color: #e9ecef;
}
</style>
